<template>
  <Box>
    <div v-if="showTitleInCard" class="px-6 pt-6 pb-4">
      <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter text-center">Order Summary</h2>
    </div>

    <V2CartItems
      v-if="hasV2CartItems"
      :cart-items="v2CartItems"
      :store-data="v2StoreData"
      :show-prices="v2Cart?.show_prices !== false"
      :read-only="readOnly"
      :enable-gift-card-view="enableGiftCardView"
      @view-gift-cards="$emit('view-gift-cards')"
    />
    <CartItems
      v-else
      :cart-items="list"
      :bottle="bottle"
      :read-only="readOnly"
      :enable-gift-card-view="enableGiftCardView"
      @view-gift-cards="$emit('view-gift-cards')"
    />
    <div v-if="upcomingMealPlanCharge" data-test="upcoming-meal-plan-charge" class="px-6 border-t border-gray-200">
      <OrderSummaryItem
        name="Meal plan"
        :variant-text="mealPlanItemDescription"
        :price-formatted="mealPlanItemPrice"
        :quantity="1"
        :editable="false"
      />
    </div>
    <div v-if="!readOnly && !bottleIsPaid" class="border-t border-gray-200 py-3 px-6 flex items-center gap-6">
      <button @click="addItems" class="text-sm text-green-700 hover:text-green-800 font-medium flex items-center">
        <svg
          class="h-4 w-4 mr-1"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Add Items
      </button>
      <button @click="goToCart" class="text-sm text-green-700 hover:text-green-800 font-medium flex items-center">
        <svg
          class="h-4 w-4 mr-1"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
        Edit Cart
      </button>
    </div>
    <DiscountCodeModule
      v-if="hasDiscountCodeBox"
      ref="discountModule"
      @discount-updated="updateDiscount"
      @discount-removed="removeDiscount"
    />
    <OrderSummaryTotals
      v-if="checkoutAttributes"
      :subtotal-formatted="checkoutAttributes.cart_total_amount.format"
      :discount="discountTotalsLine"
      :fulfillment-discount="fulfillmentDiscountTotalsLine"
      :delivery-formatted="deliveryTotalsLine"
      :delivery-original-formatted="deliveryOriginalFormatted"
      :delivery-label="fulfillmentFeeLabel"
      :taxes="taxesTotalsLine"
      :tip="tipTotalsLine"
      :credits="creditsLines"
      :todays-total-formatted="todaysTotalFormatted"
    />
    <OrderSummarySavingsBanner :total-formatted="savingsBannerTotalFormatted" :lines="savingsLines" />
  </Box>
</template>
<script>
import DiscountCodeModule from '@/components/Checkout/DiscountCodeModule'
import mapRelationship from '@/utils/mapRelationship'
import Box from '@/components/Molecules/Box'
import SecondaryButton from '../Molecules/SecondaryButton.vue'
import CartItems from '@/components/Checkout/CartItems'
import OrderSummaryItem from '@/components/Checkout/OrderSummaryItem'
import V2CartItems from '@/components/v2/V2CartItems'
import OrderSummarySavingsBanner from '@/components/Checkout/OrderSummarySavingsBanner'
import OrderSummaryTotals from '@/components/Checkout/OrderSummaryTotals'
import { posthog, getCommonTrackingProperties } from '@/composables/usePosthog'
import { formatPrice } from '@/composables/useCartMutation'
import { customerFeeCents, totalsLinesFromCheckout } from '@/utils/orderSummaryLines'

export default {
  components: {
    Box,
    DiscountCodeModule,
    SecondaryButton,
    CartItems,
    V2CartItems,
    OrderSummaryItem,
    OrderSummarySavingsBanner,
    OrderSummaryTotals
  },
  props: {
    list: {
      type: Array,
      required: true
    },
    checkoutAttributes: {
      type: Object,
      required: false,
      default: null
    },
    useNeutralTheme: {
      type: Boolean,
      required: false,
      default: false
    },
    hasDiscountCodeBox: {
      type: Boolean,
      required: false,
      default: false
    },
    hasDiscountCodeList: {
      type: Boolean,
      required: false,
      default: false
    },
    passedBottle: {
      type: Object,
      required: false,
      default: null
    },
    showTitleInCard: {
      type: Boolean,
      required: false,
      default: false
    },
    v2CartItems: {
      type: Array,
      required: false,
      default: () => []
    },
    v2StoreData: {
      type: Object,
      required: false,
      default: null
    },
    v2Cart: {
      type: Object,
      required: false,
      default: null
    },
    enableGiftCardView: {
      type: Boolean,
      required: false,
      default: false
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['view-gift-cards'],
  computed: {
    hasV2CartItems() {
      return this.v2CartItems && this.v2CartItems.length > 0
    },
    checkout() {
      return this.$store.getters.checkout
    },
    bottle() {
      return this.passedBottle || this.$store.getters.currentBottle
    },
    bottleIsPaid() {
      return this.bottle?.attributes?.payment_status === 'paid'
    },
    cart() {
      return this.$store.getters.currentCart
    },
    membershipSavingsCents() {
      return this.v2Cart?.membership_savings?.cents || this.cart?.attributes?.membership_savings?.cents || 0
    },
    pricingTierSavingsCents() {
      return this.v2Cart?.pricing_tier_savings?.cents || this.cart?.attributes?.pricing_tier_savings?.cents || 0
    },
    hasMembershipSavings() {
      return this.membershipSavingsCents > 0
    },
    hasPricingTierSavings() {
      return this.pricingTierSavingsCents > 0
    },
    mealPlanSavingsCents() {
      return this.v2Cart?.meal_plan?.savings_cents || this.cart?.attributes?.meal_plan_savings?.cents || 0
    },
    hasMealPlanSavings() {
      return this.mealPlanSavingsCents > 0
    },
    membershipSavingsFormat() {
      return this.v2Cart?.membership_savings?.format || this.cart?.attributes?.membership_savings?.format
    },
    pricingTierSavingsFormat() {
      return this.v2Cart?.pricing_tier_savings?.format || this.cart?.attributes?.pricing_tier_savings?.format
    },
    mealPlanSavingsFormat() {
      if (this.v2Cart?.meal_plan?.savings_cents > 0) {
        return formatPrice(this.v2Cart.meal_plan.savings_cents, this.v2StoreData?.currency || 'USD')
      }
      return this.cart?.attributes?.meal_plan_savings?.format || null
    },
    pricingTierName() {
      return this.v2Cart?.pricing_tier?.name || this.pricingTier?.attributes?.title
    },
    membershipTierName() {
      return this.membershipTier?.attributes?.tier_name
    },
    membershipTier() {
      return this.$store.getters.membershipTier
    },
    pricingTier() {
      return this.$store.getters.pricingTierWithID(this.cart?.relationships?.pricing_tier?.data?.id)
    },
    unavailableItems() {
      return this.bottle?.relationships?.removed_category_product_store_pairs_still_in_cart?.data
    },
    hasFulfillmentAmount() {
      return this.checkout?.attributes?.fulfillment_amount && this.checkout?.attributes?.fulfillment_amount.cents > 0
    },
    hasFufillmentAmountOff() {
      return (
        this.checkout?.attributes?.fulfillment_amount_off && this.checkout?.attributes?.fulfillment_amount_off.cents > 0
      )
    },
    hasDiscountCodeAmount() {
      return (
        this.checkout?.attributes?.discount_code_amount && this.checkout?.attributes?.discount_code_amount.cents > 0
      )
    },
    hasFulfillmentAmountOff() {
      return (
        this.checkout?.attributes?.order_fulfillment_discount_amount &&
        this.checkout?.attributes?.order_fulfillment_discount_amount.cents > 0
      )
    },
    hasAccountCreditAmount() {
      return (
        this.checkout?.attributes?.account_credit_amount && this.checkout?.attributes?.account_credit_amount.cents > 0
      )
    },
    hasImmediatePassRewardAmount() {
      return (
        this.checkout?.attributes?.pass_signup_credit_amount &&
        this.checkout?.attributes?.pass_signup_credit_amount.cents > 0
      )
    },
    hasPassCreditAmount() {
      return this.checkout?.attributes?.pass_credit_amount && this.checkout?.attributes?.pass_credit_amount.cents > 0
    },
    hasTaxAmount() {
      return this.checkout?.attributes?.tax_amount && this.checkout?.attributes?.tax_amount.cents > 0
    },
    hasTipAmount() {
      return this.checkout?.attributes?.tip_amount && this.checkout?.attributes?.tip_amount.cents > 0
    },
    hasFeeToChargeCustomer() {
      return customerFeeCents(this.checkout?.attributes) > 0
    },
    hasBeenPaid() {
      return this.checkout?.attributes?.payment_status == 'paid'
    },
    hasAmountRemainingToCharge() {
      return (this.hasAccountCreditAmount || this.hasPassCreditAmount) && !this.hasBeenPaid
    },
    supportsSelectionScreen() {
      return this.bottle?.attributes?.supports_selection_screen
    },
    cartSavingsCents() {
      return this.membershipSavingsCents + this.pricingTierSavingsCents + this.mealPlanSavingsCents
    },
    discountCodeSavingsCents() {
      return this.checkoutAttributes?.discount_code_amount?.cents || 0
    },
    fulfillmentDiscountSavingsCents() {
      return this.checkoutAttributes?.order_fulfillment_discount_amount?.cents || 0
    },
    totalSavingsAmount() {
      const serialized = this.checkoutAttributes?.total_savings?.cents
      if (serialized != null) return serialized
      return this.cartSavingsCents + this.discountCodeSavingsCents + this.fulfillmentDiscountSavingsCents
    },
    upcomingMealPlanCharge() {
      // The plan fee is a separate charge that fires right after the ORDER
      // payment when the customer is newly enrolling (or has a pending
      // enrollment). Hidden once the subscription is actively billing.
      const tier = this.$store.getters.membershipTier
      const plan = mapRelationship(this.$store.state, tier?.relationships?.meal_plan?.data)
      if (!plan) return null
      const customer = this.$store.getters.customer
      const sub = mapRelationship(this.$store.state, customer?.relationships?.meal_plan_subscription?.data)
      const samePlan = sub && parseInt(sub.attributes.meal_plan_id) === parseInt(plan.id)
      if (samePlan && sub.attributes.status !== 'pending_first_payment') return null
      return plan
    },
    mealPlanBillingLabel() {
      const interval = this.upcomingMealPlanCharge?.attributes?.billing_interval
      return { weekly: 'week', every_two_weeks: '2 weeks', monthly: 'month' }[interval] || 'cycle'
    },
    mealPlanItemDescription() {
      const count = this.upcomingMealPlanCharge?.attributes?.item_count
      return `${count} meals per ${this.mealPlanBillingLabel} - billed separately once this order is placed`
    },
    mealPlanItemPrice() {
      return `${this.upcomingMealPlanCharge.attributes.price_formatted}/${this.mealPlanBillingLabel}`
    },
    savingsLines() {
      const lines = []
      if (this.hasMembershipSavings) {
        lines.push({
          label: this.membershipTierName || 'Membership savings',
          amountFormatted: this.membershipSavingsFormat
        })
      }
      if (this.hasPricingTierSavings) {
        lines.push({
          label: this.pricingTierName || 'Pricing discount',
          amountFormatted: this.pricingTierSavingsFormat
        })
      }
      if (this.hasMealPlanSavings) {
        lines.push({
          label: 'Meal plan',
          amountFormatted: this.mealPlanSavingsFormat
        })
      }
      if (this.discountTotalsLine) lines.push(this.discountTotalsLine)
      if (this.fulfillmentDiscountTotalsLine) lines.push(this.fulfillmentDiscountTotalsLine)
      return lines
    },
    savingsBannerTotalFormatted() {
      if (this.totalSavingsAmount <= 0) return ''
      return (
        this.checkoutAttributes?.total_savings?.format ||
        formatPrice(this.totalSavingsAmount, this.v2StoreData?.currency || 'USD')
      )
    },
    totalsLines() {
      return totalsLinesFromCheckout(this.checkoutAttributes, {
        fulfillmentMethodType: this.$store.getters.fulfillmentMethod?.attributes?.type
      })
    },
    creditsLines() {
      return this.totalsLines?.credits || []
    },
    discountTotalsLine() {
      return this.totalsLines?.discount || null
    },
    fulfillmentDiscountTotalsLine() {
      return this.totalsLines?.fulfillmentDiscount || null
    },
    deliveryTotalsLine() {
      return this.totalsLines?.deliveryFormatted || null
    },
    deliveryOriginalFormatted() {
      return this.totalsLines?.deliveryOriginalFormatted || null
    },
    fulfillmentFeeLabel() {
      return this.totalsLines?.deliveryLabel || 'Delivery Fee'
    },
    taxesTotalsLine() {
      return this.totalsLines?.taxes || null
    },
    tipTotalsLine() {
      return this.totalsLines?.tip || null
    },
    todaysTotalFormatted() {
      return this.totalsLines?.todaysTotalFormatted || ''
    }
  },
  methods: {
    async updateDiscount(data) {
      try {
        const result = await this.$store.dispatch('addDiscountCode', data)
        if (result && result.errors) {
          this.$refs.discountModule?.handleDiscountError(result.errors)
        } else {
          this.$refs.discountModule?.clearForm()
        }
        return result
      } catch (error) {
        this.$refs.discountModule?.handleDiscountError(error)
        return { errors: error }
      }
    },
    removeDiscount(data) {
      this.$store.dispatch('removeAppliedDiscountCode', data)
    },
    goToCart() {
      const trackingProperties = {
        ...getCommonTrackingProperties(this),
        cart_size: this.$store.getters.cartSize
      }
      posthog.capture('user_edited_cart_from_checkout', trackingProperties)

      this.$router.push({ name: 'Selection', params: { bottleid: this.bottle?.id }, query: { showCart: 'y' } })
    },
    addItems() {
      this.$router.push({ name: 'Selection', params: { bottleid: this.bottle?.id } })
    }
  }
}
</script>
